import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { NumberInput } from "@/components/ui/input-number";
import { ColorPicker } from "@/components/ui/color-picker";
import { TailwindColorSelectorPopover } from "@/components/editor/themes/components/tailwind-color-selector-popover";

import PropertySection from "../PropertySection";

interface ShadowsSectionProps {
    localOpacity: number;
    textShadow: { color: string; x: number; y: number; blur: number };
    boxShadow: {
        color: string;
        x: number;
        y: number;
        blur: number;
        spread: number;
    };
    onOpacityChange: (value: number[]) => void;
    onOpacityCommit: (value: number[]) => void;
    onShadowChange: (
        type: "text" | "box",
        shadow: {
            color: string;
            x: number;
            y: number;
            blur: number;
            spread?: number;
        }
    ) => void;
}

export const ShadowsSection = ({
    localOpacity,
    textShadow,
    boxShadow,
    onOpacityChange,
    onOpacityCommit,
    onShadowChange,
}: ShadowsSectionProps) => {
    const renderShadowInput = (
        shadow: typeof textShadow | typeof boxShadow,
        type: "text" | "box",
        field: "x" | "y" | "blur" | "spread",
        label: string
    ) => (
        <div>
            <NumberInput
                suffixLabel={label}
                value={(shadow as any)[field]}
                onValueChange={(value) => {
                    const numValue = value ?? 0;
                    onShadowChange(type, {
                        ...shadow,
                        [field]: numValue,
                    } as any);
                }}
                placeholder="0"
                decimalScale={0}
                fixedDecimalScale
            />
        </div>
    );

    return (
        <PropertySection title="Shadows & Effects">
            <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">
                        Opacity
                    </Label>
                    <span className="text-xs text-muted-foreground tabular-nums">
                        {localOpacity}%
                    </span>
                </div>
                <Slider
                    value={[localOpacity]}
                    onValueChange={onOpacityChange}
                    onValueCommit={onOpacityCommit}
                    max={100}
                    min={0}
                    step={1}
                    className="w-full"
                />
            </div>
            <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">
                    Text Shadow Color
                </Label>
                <div className="flex items-center gap-2">
                    <ColorPicker
                        value={textShadow.color}
                        onChange={(value) =>
                            onShadowChange("text", {
                                ...textShadow,
                                color: value,
                            })
                        }
                        className="flex-shrink-0 h-8 w-8 border-border"
                    />
                    <TailwindColorSelectorPopover
                        value={textShadow.color}
                        onSelect={(color) =>
                            onShadowChange("text", {
                                ...textShadow,
                                color,
                            })
                        }
                    />
                    <Input
                        value={textShadow.color}
                        onChange={(e) =>
                            onShadowChange("text", {
                                ...textShadow,
                                color: e.target.value,
                            })
                        }
                        className="h-8 bg-background border-border text-foreground text-xs font-mono"
                        placeholder="rgb(0, 0, 0)"
                    />
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                {renderShadowInput(textShadow, "text", "x", "X")}
                {renderShadowInput(textShadow, "text", "y", "Y")}
                {renderShadowInput(textShadow, "text", "blur", "Blur")}
            </div>
            <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">
                    Box Shadow Color
                </Label>
                <div className="flex items-center gap-2">
                    <ColorPicker
                        value={boxShadow.color}
                        onChange={(value) =>
                            onShadowChange("box", {
                                ...boxShadow,
                                color: value,
                            })
                        }
                        className="flex-shrink-0 h-8 w-8 border-border"
                    />
                    <TailwindColorSelectorPopover
                        value={boxShadow.color}
                        onSelect={(color) =>
                            onShadowChange("box", {
                                ...boxShadow,
                                color,
                            })
                        }
                    />
                    <Input
                        value={boxShadow.color}
                        onChange={(e) =>
                            onShadowChange("box", {
                                ...boxShadow,
                                color: e.target.value,
                            })
                        }
                        className="h-8 bg-background border-border text-foreground text-xs font-mono"
                        placeholder="rgb(0, 0, 0)"
                    />
                </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
                {renderShadowInput(boxShadow, "box", "x", "X")}
                {renderShadowInput(boxShadow, "box", "y", "Y")}
                {renderShadowInput(boxShadow, "box", "blur", "Blur")}
                {renderShadowInput(boxShadow, "box", "spread", "Spread")}
            </div>
        </PropertySection>
    );
};
